import { useEffect, useRef, useState, useCallback } from 'react';
import { Sparkles, X } from 'lucide-react';
import { promptOptimizeAPI } from '../../services/api';
import toast from '../../components/Toast';
import type { GenerationMode } from './types';

interface PromptInputProps {
  value: string;
  onChange: (value: string) => void;
  mode: GenerationMode;
  /** 参考图列表，用于 @图N 引用 chip（img2img / inpaint） */
  referenceImages?: string[];
  /** 缩略图 src 解析（处理 data:URL / 本地 API URL 等） */
  resolveImageSrc?: (raw: string) => string;
}

const MAX_LENGTH = 4000;
// 兼容 @图1 / @图 1 / @image1 / @img1
const REF_RE = /@(?:图|image|img)\s*(\d+)/gi;

export function PromptInput({
  value,
  onChange,
  mode,
  referenceImages,
  resolveImageSrc,
}: PromptInputProps) {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeEnabled, setOptimizeEnabled] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const lastSyncedTextRef = useRef<string>('');

  useEffect(() => {
    promptOptimizeAPI
      .getStatus()
      .then((res) => setOptimizeEnabled(res.isEnabled))
      .catch(() => setOptimizeEnabled(false));
  }, []);

  const getPlaceholder = () => {
    switch (mode) {
      case 'img2img':
        return '描述你想基于参考图生成的内容；点下方"插入引用"可在 prompt 里指代具体参考图';
      case 'inpaint':
        return '描述你想在蒙版区域生成的内容，例如：把红框区域换成一只橙色的猫';
      default:
        return '描述你想生成的图片，例如：一只可爱的猫咪在阳光下打盹';
    }
  };

  // 把纯文本（含 @图N）渲染成 DOM
  const renderToEditor = useCallback(
    (text: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      while (editor.firstChild) editor.removeChild(editor.firstChild);

      let lastIndex = 0;
      let match: RegExpExecArray | null;
      const re = new RegExp(REF_RE.source, REF_RE.flags);
      while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
          editor.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }
        editor.appendChild(buildChip(match[1], referenceImages, resolveImageSrc));
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        editor.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      lastSyncedTextRef.current = text;
    },
    [referenceImages, resolveImageSrc]
  );

  useEffect(() => {
    if (value === lastSyncedTextRef.current) return;
    renderToEditor(value);
  }, [value, renderToEditor]);

  const serializeEditor = useCallback((): string => {
    const editor = editorRef.current;
    if (!editor) return '';
    let result = '';
    editor.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const refIndex = el.getAttribute('data-ref-index');
        if (refIndex) result += `@图${refIndex}`;
        else result += el.textContent || '';
      }
    });
    return result;
  }, []);

  const handleInput = () => {
    const text = serializeEditor();
    if (text.length > MAX_LENGTH) {
      renderToEditor(lastSyncedTextRef.current);
      return;
    }
    lastSyncedTextRef.current = text;
    onChange(text);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const insertReference = (refIndex: number) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const chip = buildChip(String(refIndex), referenceImages, resolveImageSrc);
    const space = document.createTextNode(' ');

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(space);
      range.insertNode(chip);
      range.setStartAfter(space);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editor.appendChild(chip);
      editor.appendChild(space);
    }
    handleInput();
  };

  const handleOptimize = async () => {
    if (!value.trim() || isOptimizing) return;

    setIsOptimizing(true);
    let optimizedText = '';

    try {
      await promptOptimizeAPI.streamOptimize(
        value,
        mode,
        (chunk) => {
          optimizedText += chunk;
          onChange(optimizedText);
        },
        (error) => {
          console.error('优化失败:', error);
          toast.error('提示词优化失败: ' + error);
        },
        () => setIsOptimizing(false)
      );
    } catch (error) {
      setIsOptimizing(false);
      toast.error('优化失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  const refCount = referenceImages?.length || 0;
  const showRefBar = mode === 'img2img' && refCount > 0;

  return (
    <div>
      <label className="text-sm font-medium text-light-text mb-3 block">创作内容</label>
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!isOptimizing}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          className={`w-full h-32 bg-white border border-light-border rounded-xl px-4 py-3 text-light-text leading-relaxed overflow-y-auto outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-all ${
            isOptimizing ? 'bg-blue-50/50' : ''
          }`}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        />
        {value.length === 0 && (
          <div className="pointer-events-none absolute top-3 left-4 text-sm text-light-muted">
            {getPlaceholder()}
          </div>
        )}
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          {value && !isOptimizing && (
            <button
              onClick={() => onChange('')}
              className="p-1.5 text-light-muted hover:text-light-text transition-colors rounded-lg hover:bg-light-hover"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {optimizeEnabled && (
            <button
              onClick={handleOptimize}
              disabled={isOptimizing || !value.trim()}
              className={`p-1.5 transition-colors rounded-lg ${
                isOptimizing
                  ? 'text-accent-blue animate-pulse bg-blue-100'
                  : value.trim()
                  ? 'text-light-muted hover:text-accent-blue hover:bg-light-hover'
                  : 'text-light-muted/50 cursor-not-allowed'
              }`}
              title={isOptimizing ? 'AI优化中...' : 'AI优化提示词'}
            >
              <Sparkles className={`w-4 h-4 ${isOptimizing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {showRefBar && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-light-muted mr-1">插入引用：</span>
          {Array.from({ length: refCount }).map((_, idx) => {
            const num = idx + 1;
            const raw = referenceImages![idx];
            const src = resolveImageSrc ? resolveImageSrc(raw) : raw;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => insertReference(num)}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-light-border bg-white hover:border-accent-blue hover:bg-accent-blue/5 text-xs"
                title={`插入 @图${num}`}
              >
                <img src={src} alt="" className="w-5 h-5 rounded object-cover" />
                <span className="text-light-text">@图{num}</span>
              </button>
            );
          })}
        </div>
      )}

      {isOptimizing && (
        <p className="text-xs text-accent-blue mt-2 flex items-center gap-1">
          <Sparkles className="w-3 h-3 animate-spin" />
          AI正在优化提示词...
        </p>
      )}
    </div>
  );
}

function buildChip(
  refIndex: string,
  referenceImages: string[] | undefined,
  resolveImageSrc?: (raw: string) => string
): HTMLSpanElement {
  const idx = parseInt(refIndex, 10);
  const chip = document.createElement('span');
  chip.setAttribute('contenteditable', 'false');
  chip.setAttribute('data-ref-index', String(idx));
  chip.className =
    'inline-flex items-center gap-1 mx-0.5 px-1.5 py-0.5 rounded-md bg-accent-blue/10 text-accent-blue text-xs align-middle select-none';
  chip.style.cursor = 'default';

  const raw = referenceImages?.[idx - 1];
  if (raw) {
    const img = document.createElement('img');
    img.src = resolveImageSrc ? resolveImageSrc(raw) : raw;
    img.alt = '';
    img.className = 'w-4 h-4 rounded object-cover';
    chip.appendChild(img);
  }
  const label = document.createElement('span');
  label.textContent = `图${idx}`;
  chip.appendChild(label);

  return chip;
}
